import { useEffect, useRef } from 'react';

import { useQuery } from '@apollo/client/react';

import { GeocodeDocument } from '@graphql/platform';

/**
 * Disposal eligibility for the current ZIP.
 * Returns undefined while a new ZIP's geocode is unresolved or the query
 * failed, so callers do not reuse eligibility from a previous ZIP or treat a
 * failed lookup as ineligible. Keeps the last result for the same ZIP during
 * refetches so the disposal step does not flicker.
 */
export function useEligibleForDisposalZIP(zip?: string): boolean | undefined {
  const { data, loading, error } = useQuery(GeocodeDocument, {
    variables: { zip: zip ?? '' },
    skip: !zip,
  });

  const eligibility = data ? !!data.geocode?.eligibleForDisposal : undefined;
  const cacheRef = useRef<{ zip?: string; eligibility?: boolean }>({});

  useEffect(() => {
    if (zip && !error && !loading && eligibility !== undefined) {
      cacheRef.current = { zip, eligibility };
    }
  }, [eligibility, error, loading, zip]);

  if (!zip) {
    return false;
  }

  if (error) {
    return undefined;
  }

  if (loading) {
    return cacheRef.current.zip === zip
      ? cacheRef.current.eligibility
      : undefined;
  }

  return eligibility ?? false;
}
